import React, { forwardRef, useRef } from 'react';
import dayjs from 'dayjs';
import { ListLayout } from './list';
import { MiniCalendar } from './mini-calendar';
import { Filters } from './filters';
import { GridLayout } from './grid';
import { Weekdays } from './weekdays';
import { CombinedFilterData, Month } from '../helpers/common-types';
import { useMediaQuery } from 'react-responsive';
import { useAppSelector } from '../app/hooks';
import { A11yDialog } from 'react-a11y-dialog';
import A11yDialogInstance from 'a11y-dialog';
import { SvgIcon } from './svg-icon';
import { MonthDropdown } from './month-dropdown';

export type CalendarLayoutProps = {
    filteredEvents: Month[],
    combinedFilterData: CombinedFilterData
}

export type CalendarMobileBarProps = {
    filteredEvents: Month[],
    combinedFilterData: CombinedFilterData,
    showCalendar: boolean
}

/*
 * List layout with mini-nav on left
 */
export const CalendarLayoutA = forwardRef<HTMLDivElement, CalendarLayoutProps>((props, ref) => {
    const { filteredEvents, combinedFilterData } = props;
    const layoutBreakpoint = useAppSelector(state => state.settings.layoutBreakpoint);
    const belowLayoutBreakpoint = useMediaQuery({ query: `(max-width: ${layoutBreakpoint})` });

    return (
        <div className="">
            {
                belowLayoutBreakpoint ?
                    <ModalNavAndFiltersBar {...{ combinedFilterData, filteredEvents }} showCalendar={true} ref={ref} />
                    :
                    <Filters showMonthDropdown={false} {...{ combinedFilterData }} ref={ref} />
            }
            <div className="ace-calendar-layout ace-calendar-container ace-bg-gray">
                <div className="ace-layout-3-9 ace-layout">
                    {
                        !belowLayoutBreakpoint &&
                        <MiniCalendar {...{ filteredEvents }} />
                    }
                    <ListLayout {...{ filteredEvents, combinedFilterData }} />
                </div>
            </div>
        </div>
    )
})

/*
 * Infinite scroll list
 */
export const CalendarLayoutB = forwardRef<HTMLDivElement, CalendarLayoutProps>((props, ref) => {
    const { filteredEvents, combinedFilterData } = props;
    const layoutBreakpoint = useAppSelector(state => state.settings.layoutBreakpoint);
    const belowLayoutBreakpoint = useMediaQuery({ query: `(max-width: ${layoutBreakpoint})` });

    return (
        <div className="stack">
            {
                belowLayoutBreakpoint ?
                    <ModalNavAndFiltersBar {...{ combinedFilterData, filteredEvents }} showCalendar={false} ref={ref} />
                    :
                    <Filters showMonthDropdown={true} {...{ combinedFilterData }} ref={ref} />
            }
            <div className="ace-calendar-layout ace-calendar-container ace-bg-gray">
                <ListLayout {...{ filteredEvents, combinedFilterData }} />
            </div>
        </div>
    )
})

/*
 * Grid - hidden on mobile from App.tsx
 */
export const CalendarLayoutC = forwardRef<HTMLDivElement, CalendarLayoutProps>((props, ref) => {
    const { filteredEvents, combinedFilterData } = props;
    return (
        <div className="">
            <Filters showMonthDropdown={true} {...{ combinedFilterData }} ref={ref} />
            <Weekdays />
            <div className="ace-calendar-layout ace-calendar-container ace-bg-gray">
                <GridLayout {...{ filteredEvents }} />
            </div>
        </div>
    )
})

/*
 * Nav bar containing modal triggers for filters and mini-calendar nav, with promo bar. Alternate option to display month dropdown instead of mini-cal.
 */
export const ModalNavAndFiltersBar = forwardRef<HTMLDivElement, CalendarMobileBarProps>((props, ref) => {
    const { showCalendar, ...contentProps } = props;
    const filterModal = useRef<A11yDialogInstance>();
    const dateModal = useRef<A11yDialogInstance>();
    const { filterOpenButtonLabel, calendarOpenButtonLabel, closeDialogButtonText,
        calendarFiltersHeading, calendarNavigationHeading } = useAppSelector(state => state.settings);

    const modalClasses = {
        container: 'dialog-container', overlay: 'dialog-overlay', dialog: 'dialog-content', title: 'h3-style', closeButton: 'dialog-close'
    };

    return (
        <div className="ace-cal-filters-container ace-bg-gray" ref={ref}>
            <div className="ace-cal-filters-mobile">
                {
                    showCalendar ?
                        <>
                            <button type="button" className="btn-secondary" onClick={() => dateModal.current?.show()}>
                                <svg className="svg-icon-stroke svg-icon-text" aria-hidden="true" focusable="false">
                                    <use xmlnsXlink="http://www.w3.org/1999/xlink" xlinkHref="#icon-calendar"></use>
                                </svg>
                                <span>{calendarOpenButtonLabel}</span>
                            </button>
                            
                            <A11yDialog
                                id="date_modal"
                                title={calendarNavigationHeading}
                                dialogRef={instance => (dateModal.current = (instance as A11yDialogInstance))}
                                classNames={modalClasses}
                                closeButtonLabel={closeDialogButtonText}>
                                <MiniCalendar {...contentProps} dayClickedCallback={() => dateModal.current?.hide()} />
                                </A11yDialog>
                        </>
                        :
                        <MonthDropdown hideLabel={true} />
                }
                <button type="button" className="btn" onClick={() => filterModal.current?.show()}>
                    <SvgIcon iconId={"icon-filter"} />
                    <span>{filterOpenButtonLabel}</span>
                </button>
                <A11yDialog
                    id="filters_modal"
                    title={calendarFiltersHeading}
                    dialogRef={instance => (filterModal.current = (instance as A11yDialogInstance))}
                    classNames={modalClasses}
                    closeButtonLabel={closeDialogButtonText}>
                    <Filters {...contentProps} showMonthDropdown={false} includeWrapper={false} filterCloseCallback={() => filterModal.current?.hide()} />
                </A11yDialog>
            </div>
        </div>
    )
})